import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildAutomationEditPath } from '../../../../../app-routes';
import { Button } from '../../../../../components/ui/button';
import { TemplateNameAndDescriptionDialog } from '../../template-dialog-helpers';
import type { AutomationTemplate } from '../../templates.types';
import { useCopyTemplate } from './useCopyTemplate';
import styles from './TemplatesTableRow.module.css';

interface CopyTemplateButtonProps {
  template: AutomationTemplate;
}

export function CopyTemplateButton({ template }: CopyTemplateButtonProps) {
  const navigate = useNavigate();
  const copyMutation = useCopyTemplate();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const sourceName = template.contents?.name || template.name;
    setName(`${sourceName} (Copy)`);
    setDescription(template.contents?.description || '');
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setName('');
    setDescription('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { created, templateId } = await copyMutation.mutateAsync({
      sourceTemplateId: template.id,
      customName: name,
      customDescription: description,
    });

    // On name collision the dialog stays open so the user can pick a new name.
    if (created && templateId) {
      handleClose();
      navigate(buildAutomationEditPath(templateId));
    }
  };

  return (
    <>
      <Button onClick={handleOpen} variant="ghost" size="sm" className={styles.copyButton}>
        Copy
      </Button>
      {isOpen && (
        <TemplateNameAndDescriptionDialog
          templateName={name}
          description={description}
          onTemplateNameChange={setName}
          onDescriptionChange={setDescription}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onClose={handleClose}
          isLoading={copyMutation.isPending}
          title="Copy Template"
          submitLabel="Copy Template"
          submittingLabel="Copying..."
          cancelLabel="Cancel"
        />
      )}
    </>
  );
}
